import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, ToggleButton, useTheme } from '@mui/material'
import { DateTime } from 'luxon'
import { callAxios } from '../hooks/useAxios'
import { Device, Port, TimeString } from '../types/models'
import { useTranslation } from 'react-i18next'
import { RadioSelector } from '../components/atoms/RadioSelector'
import { PowerHistoryChart } from './charts/PowerHistoryChart'
import { BatteryHistoryChart } from './charts/BatteryHistoryChart'
import { ScaleLabeling, PowerData, BatteryData, ChartType } from '../types/charts'
import { MOBILE_SIZE } from '../types/constants'
import { range } from '../utils/time'
import { mergeHistory, pointsForScale, stampMs } from '../utils/history'
import { useApp } from '../hooks/useApp'
import LineAxisIcon from '@mui/icons-material/LineAxis'
import BarChartIcon from '@mui/icons-material/BarChart'

export interface PortHistoryProps {
  device: Device
  port: Port
  variant?: 'primary' | 'secondary'
}

type ScaleAnchor = {
  scale: ScaleLabeling
  // When non-null, fetching + initial view are centered on this time (epoch
  // ms) instead of the default "now" window. Lets users zoom in on the same
  // moment across scales.
  anchorMs: number | null
}

function isoFromMs(ms: number): TimeString {
  return DateTime.fromMillis(ms, { zone: 'utc' }).toISO() as TimeString
}

function computeRanges(sa: ScaleAnchor) {
  const [defMin, defMax, defFetch] = range(sa.scale)
  const defMaxMs = DateTime.fromISO(defMax).toMillis()
  const defMinMs = DateTime.fromISO(defMin).toMillis()
  const visibleWindowMs = defMaxMs - defMinMs

  if (sa.anchorMs === null) {
    return {
      chartFrom: defMin as TimeString,
      chartTo: defMax as TimeString,
      fetchFrom: defFetch as TimeString,
      fetchTo: defMax as TimeString,
      defMaxMs,
      pageMs: defMaxMs - DateTime.fromISO(defFetch).toMillis(),
    }
  }

  const halfVis = visibleWindowMs / 2
  // Load one visible window's worth on each side of the anchor.
  const loadHalf = visibleWindowMs

  const visFromMs = sa.anchorMs - halfVis
  const visToMs = Math.min(sa.anchorMs + halfVis, defMaxMs)
  const fetchFromMs = sa.anchorMs - loadHalf
  const fetchToMs = Math.min(sa.anchorMs + loadHalf, defMaxMs)

  return {
    chartFrom: isoFromMs(visFromMs),
    chartTo: isoFromMs(visToMs),
    fetchFrom: isoFromMs(fetchFromMs),
    fetchTo: isoFromMs(fetchToMs),
    defMaxMs,
    pageMs: fetchToMs - fetchFromMs,
  }
}

export function PortHistory(props: PortHistoryProps) {
  const { device, port, variant } = props

  const { t } = useTranslation()
  const app = useApp()
  const theme = useTheme()

  const [fetching, setFetching] = useState(true)
  const [power, setPower] = useState<PowerData[]>([])
  const [battery, setBattery] = useState<BatteryData[]>([])
  const [scaleAnchor, setScaleAnchor] = useState<ScaleAnchor>({ scale: 'minute', anchorMs: null })
  const [loadedFrom, setLoadedFrom] = useState<TimeString | null>(null)
  const [loadedTo, setLoadedTo] = useState<TimeString | null>(null)
  const [oldest, setOldest] = useState<TimeString | null>(null)
  const [chartType, setChartType] = useState<ChartType>('bar')
  // Scale the currently held points were fetched for, or null while a fetch is
  // pending. Clearing the points happens in an effect, which React runs only
  // *after* the render that follows a scale switch - that render would
  // otherwise hand the previous scale's points to the chart together with the
  // new `steps`, and e.g. bucketing four months of week data into minutes
  // makes hundreds of thousands of slots, which is slow enough to freeze the
  // page and used to overflow the stack outright.
  const [dataScale, setDataScale] = useState<ScaleLabeling | null>(null)

  const isLoadingLeftRef = useRef(false)
  const isLoadingRightRef = useRef(false)
  // Identity of the scale/anchor being fetched for. A response that arrives
  // after the user has moved on is dropped rather than merged into the points
  // of a scale it does not belong to.
  const pendingRef = useRef<ScaleAnchor | null>(null)
  // Latest viewport time range reported by the chart. Used as the anchor
  // when the user switches scale.
  const viewportTimeRef = useRef<{ fromMs: number, toMs: number } | null>(null)

  const { chartFrom, chartTo, fetchFrom, fetchTo, defMaxMs, pageMs } = computeRanges(scaleAnchor)
  const scale = scaleAnchor.scale
  const apiType = port.type === 'ENERGY_STORAGE' ? 'battery' : 'power'

  // Resolve oldest known data point so loadMore knows when to stop.
  useEffect(() => {
    callAxios<{ energy: TimeString | null }>({ url: `/history/oldest/${port.id}` })
      .then(res => { if (res.success) setOldest(res.data.energy) })
  }, [port.id])

  useEffect(() => {
    const pending = scaleAnchor
    pendingRef.current = pending
    setFetching(true)
    setLoadedFrom(null)
    setLoadedTo(null)
    setDataScale(null)
    viewportTimeRef.current = null
    if (apiType === 'battery') {
      setBattery([])
    } else {
      setPower([])
    }

    callAxios<PowerData[] | BatteryData[]>({
      url: `/history/${apiType}/${port.id}?from=${fetchFrom}&to=${fetchTo}&steps=${scale}`,
    }).then(res => {
      if (pendingRef.current !== pending) {
        return
      }
      setFetching(false)
      if (!res.success) {
        app.addError(res)
        return
      }
      setLoadedFrom(fetchFrom)
      setLoadedTo(fetchTo)

      if (apiType === 'battery') {
        const data = res.data as BatteryData[]
        setBattery(data.length <= 1 ? emptyBattery(fetchFrom, fetchTo) : data)
      } else {
        const data = res.data as PowerData[]
        setPower(data.length <= 1 ? emptyPower(fetchFrom, fetchTo) : data)
      }
      setDataScale(pending.scale)
    })

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scaleAnchor])

  const loadMoreLeft = useCallback(() => {
    if (isLoadingLeftRef.current || !loadedFrom || !loadedTo) return

    const oldestMs = oldest === null ? null : stampMs(oldest)
    const fromMs = DateTime.fromISO(loadedFrom).toMillis()
    if (oldestMs !== null && fromMs <= oldestMs) return

    // One page is always the size of the initial fetch window. Using the size
    // of everything loaded so far instead would double the step on every call.
    let newFromMs = fromMs - pageMs
    if (oldestMs !== null && newFromMs < oldestMs) {
      newFromMs = oldestMs
    }
    const newFrom = isoFromMs(newFromMs)

    const pending = pendingRef.current
    isLoadingLeftRef.current = true
    callAxios<PowerData[] | BatteryData[]>({
      url: `/history/${apiType}/${port.id}?from=${newFrom}&to=${loadedFrom}&steps=${scale}`,
    }).then(res => {
      isLoadingLeftRef.current = false
      if (!res.success || pendingRef.current !== pending) return
      if (apiType === 'battery') {
        setBattery(prev => mergeHistory(prev, res.data as BatteryData[]))
      } else {
        setPower(prev => mergeHistory(prev, res.data as PowerData[]))
      }
      setLoadedFrom(newFrom)
    })
  }, [loadedFrom, loadedTo, oldest, scale, port.id, apiType, pageMs])

  const loadMoreRight = useCallback(() => {
    if (isLoadingRightRef.current || !loadedFrom || !loadedTo) return
    const toMs = DateTime.fromISO(loadedTo).toMillis()
    if (toMs >= defMaxMs) return

    const newToMs = Math.min(toMs + pageMs, defMaxMs)
    const newTo = isoFromMs(newToMs)

    const pending = pendingRef.current
    isLoadingRightRef.current = true
    callAxios<PowerData[] | BatteryData[]>({
      url: `/history/${apiType}/${port.id}?from=${loadedTo}&to=${newTo}&steps=${scale}`,
    }).then(res => {
      isLoadingRightRef.current = false
      if (!res.success || pendingRef.current !== pending) return
      if (apiType === 'battery') {
        setBattery(prev => mergeHistory(prev, res.data as BatteryData[]))
      } else {
        setPower(prev => mergeHistory(prev, res.data as PowerData[]))
      }
      setLoadedTo(newTo)
    })
  }, [loadedFrom, loadedTo, scale, port.id, apiType, defMaxMs, pageMs])

  const handleViewportChange = useCallback((fromMs: number, toMs: number) => {
    viewportTimeRef.current = { fromMs, toMs }
  }, [])

  const handleScaleClick = useCallback((value: string) => {
    const newScale = value as ScaleLabeling
    if (newScale === scale) return
    const v = viewportTimeRef.current
    const anchorMs = v !== null ? (v.fromMs + v.toMs) / 2 : null
    setScaleAnchor({ scale: newScale, anchorMs })
  }, [scale])

  const handleToggleChartType = () => {
    setChartType(prev => prev === 'bar' ? 'line' : 'bar')
  }

  // Only points fetched for the scale currently on display may be charted.
  const powerData = pointsForScale(power, dataScale, scale)
  const batteryData = pointsForScale(battery, dataScale, scale)

  return <Box>
    <Box sx={{ textAlign: 'center', mb: 4, position: 'relative', display: 'flex', flexDirection: app.mobileWidth < MOBILE_SIZE ? 'column' : 'row', justifyContent: 'center' }}>
      <RadioSelector
        variant={variant || 'primary'}
        value={scale}
        disabled={fetching}
        onClick={handleScaleClick}
        values={{
          minute: t('Scale-Minute'),
          quarter: t('Scale-Quarter'),
          hour: t('Scale-Hour'),
          day: t('Scale-Day'),
          week: t('Scale-Week'),
          month: t('Scale-Month'),
          year: t('Scale-Year'),
        }}
      />
      {scale !== 'minute' && port.type !== 'ENERGY_STORAGE' &&

      <Box sx={{ width: '9%' }}>
         <ToggleButton
          sx={{ padding: 0.4, position: 'absolute', right: 0, }}
          value="check"
          onChange={handleToggleChartType}
        >
          {chartType === 'bar' && <LineAxisIcon sx={{ color: variant === 'primary' ? theme.palette.primary.main : theme.palette.secondary.main }} />}
          {chartType === 'line' && <BarChartIcon sx={{ color: variant === 'primary' ? theme.palette.primary.main : theme.palette.secondary.main }} />}
        </ToggleButton>
      </Box>

      }
    </Box>

    {port.type !== 'ENERGY_STORAGE' && <PowerHistoryChart
      id={`power-history-${device.id}-${port.id}`}
      spinner={fetching}
      data={powerData}
      onLoadMoreLeft={loadMoreLeft}
      onLoadMoreRight={loadMoreRight}
      onViewportChange={handleViewportChange}
      options={{
        type: chartType,
        variant: variant || 'primary',
        smoothening: 'mean',
        from: chartFrom,
        to: chartTo,
        steps: scale,
        includeEmpty: true,
        includeLast: true,
        timestamps: false,
        compact: true,
        panX: true,
      }}
    />}

    {port.type === 'ENERGY_STORAGE' && batteryData.length !== 0 && <BatteryHistoryChart
        id={`battery-history-${device.id}-${port.id}`}
        spinner={fetching}
        data={batteryData}
        onLoadMoreLeft={loadMoreLeft}
        onLoadMoreRight={loadMoreRight}
        onViewportChange={handleViewportChange}
        options={{
          type: 'line',
          variant: variant || 'primary',
          smoothening: 'mean',
          from: chartFrom,
          to: chartTo,
          steps: scale,
          includeEmpty: true,
          includeLast: true,
          timestamps: false,
          compact: true,
          panX: true,
        }}
    />}
  </Box>
}

function emptyBattery(from: TimeString, to: TimeString): BatteryData[] {
  const blank = { soc: 0, energy: 0, power: 0, target: 0, status: 0, min_power: 0, max_power: 0, min_target: 0, max_target: 0 }
  return [
    { ...blank, timestamp: from },
    { ...blank, timestamp: to },
  ]
}

function emptyPower(from: TimeString, to: TimeString): PowerData[] {
  return [
    { energy: 0, power: 0, timestamp: from } as PowerData,
    { energy: 0, power: 0, timestamp: to } as PowerData,
  ]
}
